import { useCallback, useMemo, useState } from 'react';
import { getArtifactDownloadUrl } from '@api/processRuns';
import type { EditorContextValue, NodeDisplayState } from '../editor/EditorContext';

const IDLE_STATE: NodeDisplayState = {
  status: 'idle',
  errorMessage: null,
  errorDetail: null,
  executionTimeMs: null,
  source: 'none',
};

const NOOP = () => {};

/**
 * Build a read-only {@link EditorContextValue} for the run detail page.
 *
 * Every mutation is a no-op. {@code runStateForNode} reads from the
 * pre-built map so the existing node components render status outlines
 * and artifact panels without any code changes.
 */
export function useRunDetailContext(
  processId: string,
  runStateMap: ReadonlyMap<string, NodeDisplayState>,
  isRunActive = false,
): EditorContextValue {
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(new Set());

  const runStateForNode = useCallback(
    (nodeId: string): NodeDisplayState => runStateMap.get(nodeId) ?? IDLE_STATE,
    [runStateMap],
  );

  const refreshArtifactUrl = useCallback(
    async (artifactId: string): Promise<string> => {
      if (!processId) return '';
      const fresh = await getArtifactDownloadUrl(processId, artifactId);
      return fresh.downloadUrl;
    },
    [processId],
  );

  const isNodeCollapsed = useCallback(
    (nodeId: string) => collapsedIds.has('__all__') || collapsedIds.has(nodeId),
    [collapsedIds],
  );

  const toggleNodeCollapsed = useCallback(
    (nodeId: string) =>
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      }),
    [],
  );

  const setAllNodesCollapsed = useCallback(
    (collapsed: boolean) =>
      setCollapsedIds(collapsed ? new Set(['__all__']) : new Set()),
    [],
  );

  return useMemo<EditorContextValue>(
    () => ({
      updateNodeData: NOOP,
      removeNode: NOOP,
      duplicateNode: NOOP,
      readOnly: true,

      runStateForNode,
      getRunGate: () => ({ canRun: false, reason: 'read_only' }),
      errorsForNode: () => [],
      publishHintsForNode: () => [],
      isNodeGenerating: () => false,
      isRunActive,
      startNodeRun: NOOP,
      cancelNodeRun: NOOP,
      // Gates are decided from the editor, so the BranchRow pills stay
      // decorative here.
      getGateWaitingForNode: () => null,
      requestGateDecision: NOOP,
      refreshArtifactUrl,

      runHistoryOpen: false,
      setRunHistoryOpen: NOOP,

      isNodeCollapsed,
      toggleNodeCollapsed,
      setAllNodesCollapsed,

      wireStyle: 'ortho',
      setWireStyle: NOOP,

      gridOn: true,
      setGridOn: NOOP,
      miniOn: true,
      setMiniOn: NOOP,

      canUndo: false,
      canRedo: false,
      undo: NOOP,
      redo: NOOP,

      selectedNodeIds: [],
      selectedEdgeIds: [],
      connectionFocus: null,
      removeSelectedNodes: NOOP,
      duplicateSelectedNodes: NOOP,

      autoArrange: NOOP,

      zoomIn: NOOP,
      zoomOut: NOOP,
      fitView: NOOP,
      recenter: NOOP,

      hasNodeRunStates: runStateMap.size > 0,
      clearNodeStatuses: NOOP,
    }),
    [
      runStateForNode,
      refreshArtifactUrl,
      isNodeCollapsed,
      toggleNodeCollapsed,
      setAllNodesCollapsed,
      runStateMap.size,
      isRunActive,
    ],
  );
}
